(() => {
  'use strict';
  const configNode = document.getElementById('ZenBraceletConfig');
  if (!configNode || window.ZenBraceletSizing) return;
  const config = JSON.parse(configNode.textContent);
  const text = config.text;
  const property = 'Requested finished bracelet size (inches)';
  const custom = config.customVariants;
  const braceletVariants = new Set(config.braceletVariants.map(String));
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  let cart = config.cart;
  let refreshTask;
  let checkoutIntent = null;
  let replayUntil = 0;
  let cartGeneration = 0;
  let verifiedGeneration = 0;
  let customHint = false;
  let mutationSequence = 0;
  const mutations = new Map();
  const recoveryKey = 'zen-bracelet-recovery-v1';
  let storageAvailable = true;
  let recovery = readRecovery();
  let refreshTimer;
  const lastVariant = new WeakMap();
  const formIntents = new WeakMap();
  let submittingFormIntent = null;
  const checkoutPass = new WeakSet();

  function measurement(value) {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (!/^\d+(?:\.\d+)?$/.test(raw)) return {ok: false, error: text.invalid};
    const number = Number(raw);
    if (!Number.isFinite(number) || number <= 0) return {ok: false, error: text.invalid};
    if (config.minimum !== null && number < config.minimum) return {ok: false, error: text.range};
    if (config.maximum !== null && number > config.maximum) return {ok: false, error: text.range};
    if (config.increment !== null && Math.abs(number / config.increment - Math.round(number / config.increment)) > 1e-8) return {ok: false, error: text.increment};
    const [whole, fraction = ''] = raw.split('.');
    const normalized = whole.replace(/^0+(?=\d)/, '') + (fraction.replace(/0+$/, '') ? '.' + fraction.replace(/0+$/, '') : '');
    return {ok: true, value: normalized};
  }

  function variantId(form) {
    return String(form?.querySelector('[name="id"]')?.value || '');
  }

  function fieldError(input, message, focus = false) {
    const field = input.closest('[data-zen-size-field]');
    const error = field?.querySelector('[data-zen-size-error]');
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
    input.setCustomValidity(message || '');
    if (error) { error.textContent = message || ''; error.hidden = !message; }
    if (focus) { input.focus(); input.scrollIntoView?.({block: 'center', behavior: 'auto'}); }
  }

  function syncForm(form) {
    const field = form.querySelector('[data-zen-product-size]');
    if (!field) return;
    const input = field.querySelector('input[data-zen-measurement]');
    const id = variantId(form);
    const active = !!custom[id];
    if (lastVariant.has(form) && lastVariant.get(form) !== id) {
      input.value = '';
      fieldError(input, '');
    }
    lastVariant.set(form, id);
    field.hidden = !active;
    input.disabled = !active;
    input.required = active;
    form.classList.toggle('zen-custom-selected', active);
    if (!active) { input.value = ''; fieldError(input, ''); }
  }

  function productURL(id) {
    const handle = custom[String(id)]?.handle || config.productsByVariant[String(id)];
    const url = new URL(root + 'products/' + handle, window.location.origin);
    url.searchParams.set('variant', String(id));
    if (config.previewThemeId) url.searchParams.set('preview_theme_id', config.previewThemeId);
    url.hash = 'zen-bracelet-size';
    return url.href;
  }

  function notice(message, href, label) {
    let box = document.getElementById('ZenBraceletNotice');
    if (!box) {
      box = document.createElement('div');
      box.id = 'ZenBraceletNotice';
      box.className = 'zen-size-notice';
      box.setAttribute('role', 'alert');
      box.tabIndex = -1;
      document.body.append(box);
    }
    box.replaceChildren();
    const p = document.createElement('p'); p.textContent = message; box.append(p);
    if (href) { const a = document.createElement('a'); a.href = href; a.textContent = label; box.append(a); }
    const close = document.createElement('button'); close.type = 'button'; close.textContent = text.dismiss;
    close.addEventListener('click', () => box.remove()); box.append(close);
    box.focus();
  }

  function validateForm(form, focus = true) {
    syncForm(form);
    const id = variantId(form);
    if (!custom[id]) return true;
    const input = form.querySelector('[data-zen-product-size] input[data-zen-measurement]');
    if (!input) { notice(text.chooseOnProduct, productURL(id), text.chooseSize); return false; }
    const answer = measurement(input.value);
    fieldError(input, answer.ok ? '' : answer.error, !answer.ok && focus);
    if (answer.ok) input.value = answer.value;
    return answer.ok;
  }

  function relevantItems(value = cart) {
    return (value?.items || []).filter(item => custom[String(item.variant_id || item.id)]);
  }

  function invalidItems(value = cart) {
    return relevantItems(value).filter(item => !measurement(item.properties?.[property]).ok);
  }

  function readRecovery() {
    let raw;
    try { raw = sessionStorage.getItem(recoveryKey) || '[]'; }
    catch { storageAvailable = false; return []; }
    try {
      const value = JSON.parse(raw);
      if (!Array.isArray(value)) throw new Error('Invalid recovery state');
      return value.filter(item => item && typeof item.id === 'string' && Number.isFinite(item.at) && ['add', 'change', 'update', 'clear'].includes(item.kind)).map(item => ({id: item.id, at: item.at, kind: item.kind, acknowledged: item.acknowledged === true}));
    } catch {
      storageAvailable = false;
      return [{id: 'unreadable-recovery', at: Date.now(), kind: 'update', acknowledged: false}];
    }
  }

  function updateRecovery(update) {
    if (storageAvailable) {
      const saved = readRecovery();
      if (storageAvailable) recovery = saved;
    }
    recovery = update(recovery);
    if (!storageAvailable) return;
    try {
      if (recovery.length) sessionStorage.setItem(recoveryKey, JSON.stringify(recovery));
      else sessionStorage.removeItem(recoveryKey);
    } catch { storageAvailable = false; }
  }

  function rememberUncertain(operation) {
    updateRecovery(items => items.some(item => item.id === operation.id) ? items : [...items, {id: operation.id, at: operation.at, kind: operation.kind, acknowledged: false}]);
  }

  function customContext() {
    return customHint || relevantItems().length > 0;
  }

  function markCustomIntent() {
    customHint = true;
    renderCartState();
  }

  function exposeFormIntent(operation) {
    submittingFormIntent = operation;
    queueMicrotask(() => { if (submittingFormIntent === operation) submittingFormIntent = null; });
  }

  function beginFormIntent(form, awaitingSubmit) {
    const data = new FormData(form);
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) properties[match[1]] = value;
    }
    properties[property] = form.querySelector('[data-zen-measurement]')?.value || '';
    const operation = {
      id: Date.now() + '-' + (++mutationSequence), at: Date.now(), kind: 'add', custom: true,
      state: 'pending', origin: 'form', form, awaitingSubmit,
      variant: variantId(form), identity: cartLineIdentity({variant_id: variantId(form), selling_plan: data.get('selling_plan') || '', properties}),
      otherIdentity: withoutMeasurementIdentity({variant_id: variantId(form), selling_plan: data.get('selling_plan') || '', properties}),
      quantity: Number(data.get('quantity') || 1)
    };
    operation.timer = setTimeout(() => {
      operation.state = 'uncertain';
      rememberUncertain(operation);
      recoveryNotice();
    }, 8000);
    mutations.set(operation.id, operation);
    formIntents.set(form, operation);
    exposeFormIntent(operation);
    customHint = true;
    cartGeneration++;
    renderCartState();
  }

  function matchingFormIntent(items, includeUncertain = false) {
    if (items.length !== 1) return null;
    const item = items[0];
    const matches = [...mutations.values()].filter(operation => operation.origin === 'form' && (operation.state === 'pending' || (includeUncertain && operation.state === 'uncertain')) && operation.variant === String(item.variant_id || item.id) && operation.identity === cartLineIdentity(item) && operation.quantity === Number(item.quantity || 1));
    return matches.length === 1 ? matches[0] : null;
  }

  function finishFormIntent(operation, completed = true) {
    clearTimeout(operation.timer);
    mutations.delete(operation.id);
    if (operation.form) formIntents.delete(operation.form);
    updateRecovery(items => items.filter(item => item.id !== operation.id));
    cartGeneration++;
    customHint = completed || [...mutations.values()].some(item => item.custom);
    renderCartState();
  }

  function blockedWriteState() {
    if (!customContext()) return '';
    let pending = false;
    mutations.forEach(operation => {
      if (operation.state === 'pending') pending = true;
      if (operation.state === 'uncertain') rememberUncertain(operation);
    });
    if (recovery.some(item => !item.acknowledged)) return 'uncertain';
    return pending ? 'pending' : '';
  }

  function renderCartState() {
    document.documentElement.classList.toggle('zen-custom-cart', customContext());
    document.documentElement.classList.toggle('zen-cart-needs-size', invalidItems().length > 0);
    document.querySelectorAll('[data-zen-cart-status]').forEach(node => {
      node.hidden = !invalidItems().length;
    });
  }

  function deadline(promise, onTimeout) {
    let timer;
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        reject(new Error('Cart request timed out'));
        onTimeout?.();
      }, 8000);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
  }

  async function refreshCart() {
    const generation = cartGeneration;
    if (refreshTask?.generation === generation) return refreshTask.promise;
    const controller = new AbortController();
    const task = {generation};
    const read = Promise.resolve()
      .then(() => nativeFetch(root + 'cart.js', {credentials: 'same-origin', cache: 'no-store', signal: controller.signal}))
      .then(response => { if (!response.ok) throw new Error('Cart read failed'); return response.json(); });
    task.promise = deadline(read, () => controller.abort())
      .then(value => {
        if (generation !== cartGeneration || !Array.isArray(value?.items)) throw new Error('Cart changed during verification');
        cart = value;
        verifiedGeneration = generation;
        if (![...mutations.values()].some(operation => operation.custom && (operation.state === 'pending' || (operation.state === 'uncertain' && !recovery.find(item => item.id === operation.id)?.acknowledged)))) customHint = false;
        renderCartState();
        return value;
      })
      .finally(() => { if (refreshTask === task) refreshTask = null; });
    refreshTask = task;
    return task.promise;
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    if (!customContext()) return;
    refreshTimer = setTimeout(() => refreshCart().catch(() => {}), 150);
  }

  function recoveryNotice() {
    notice(text.cartUncertain || text.cartError, storageAvailable ? root + 'cart' : null, text.viewCart);
    const box = document.getElementById('ZenBraceletNotice');
    if (!storageAvailable) {
      const explanation = document.createElement('p');
      explanation.textContent = text.recoveryStorageUnavailable || text.cartError;
      box.append(explanation);
    }
    const review = document.createElement('button');
    review.type = 'button';
    review.textContent = text.reviewCurrentCart || text.viewCart;
    review.addEventListener('click', showRecoveryReview);
    box.append(review);
  }

  async function showRecoveryReview() {
    try {
      const current = await refreshCart();
      const generation = verifiedGeneration;
      const reviewed = recovery.filter(item => !item.acknowledged).map(item => item.id);
      recoveryNotice();
      const box = document.getElementById('ZenBraceletNotice');
      const list = document.createElement('ul');
      current.items.forEach(item => {
        const row = document.createElement('li');
        row.textContent = (item.product_title || item.title || String(item.variant_id || item.id)) + ' × ' + item.quantity;
        if (custom[String(item.variant_id || item.id)]) row.textContent += ' — ' + text.label + ': ' + (item.properties?.[property] || text.required);
        list.append(row);
      });
      box.append(list);
      const acknowledge = document.createElement('button');
      acknowledge.type = 'button';
      acknowledge.textContent = text.reviewedCurrentCart || text.dismiss;
      acknowledge.addEventListener('click', () => {
        if (generation !== cartGeneration) { showRecoveryReview(); return; }
        updateRecovery(items => items.map(item => reviewed.includes(item.id) ? {...item, acknowledged: true} : item));
        notice(text.cartReviewed || text.cartUncertain || text.cartError);
      });
      box.append(acknowledge);
      acknowledge.focus();
    } catch { notice(text.cartError, storageAvailable ? root + 'cart' : null, text.viewCart); }
  }

  function checkWriteState() {
    const state = blockedWriteState();
    if (state === 'uncertain') recoveryNotice();
    else if (state === 'pending') notice(text.cartBusy);
    return state;
  }

  function observeCartEvent(event) {
    const payload = event?.detail?.cart || event?.detail;
    const product = event?.detail?.product;
    if (event?.type === 'ajaxProduct:added' && product && !(Number(product.status) >= 400)) {
      const intent = matchingFormIntent([product], true);
      if (intent) finishFormIntent(intent);
    }
    if (Array.isArray(payload?.items) && relevantItems(payload).length) customHint = true;
    if (custom[String(product?.variant_id || product?.id)]) customHint = true;
    if (['cart:updated', 'ajaxProduct:added', 'cart:change', 'cart:add'].includes(event?.type)) cartGeneration++;
    renderCartState();
    scheduleRefresh();
  }

  function requestItems(body) {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const items = new Map();
      const one = {id: body.get('id'), quantity: body.get('quantity'), selling_plan: body.get('selling_plan'), properties: {}};
      for (const [key, value] of body.entries()) {
        const singleProperty = /^properties\[(.+)\]$/.exec(key);
        if (singleProperty) one.properties[singleProperty[1]] = value;
        const entry = /^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/.exec(key);
        const prop = /^items\[(\d+)\]\[properties\]\[(.+)\]$/.exec(key);
        if (entry || prop) {
          const index = (entry || prop)[1];
          if (!items.has(index)) items.set(index, {properties: {}});
          if (entry) items.get(index)[entry[2]] = value;
          if (prop) items.get(index).properties[prop[2]] = value;
        }
      }
      return items.size ? [...items.values()] : [one];
    }
    if (typeof body === 'string') {
      try { const parsed = JSON.parse(body); return parsed.items || [parsed]; }
      catch { return requestItems(new URLSearchParams(body)); }
    }
    return [];
  }

  function invalidRequest(items) {
    return items.find(item => custom[String(item.id)] && !measurement(item.properties?.[property]).ok);
  }

  function cleanStandardProperties(body) {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const copy = body instanceof FormData ? new FormData() : new URLSearchParams();
      for (const [key, value] of body.entries()) copy.append(key, value);
      const id = String(copy.get('id'));
      if (braceletVariants.has(id) && !custom[id]) copy.delete('properties[' + property + ']');
      for (const [key, value] of copy.entries()) {
        const match = /^items\[(\d+)\]\[id\]$/.exec(key);
        if (match && braceletVariants.has(String(value)) && !custom[String(value)]) copy.delete('items[' + match[1] + '][properties][' + property + ']');
      }
      return copy;
    }
    if (typeof body === 'string') {
      try {
        const copy = JSON.parse(body);
        for (const item of copy.items || [copy]) {
          if (braceletVariants.has(String(item.id)) && !custom[String(item.id)] && item.properties) delete item.properties[property];
        }
        return JSON.stringify(copy);
      } catch { return cleanStandardProperties(new URLSearchParams(body)).toString(); }
    }
    return body;
  }

  window.fetch = async function(input, options) {
    const formIntent = submittingFormIntent;
    const url = new URL(typeof input === 'string' || input instanceof URL ? String(input) : input.url, window.location.href);
    const method = String(options?.method || input?.method || 'GET').toUpperCase();
    const match = url.origin === location.origin && /\/cart\/(add|change|update|clear)(?:\.js)?$/.exec(url.pathname);
    const mutation = match && method === 'POST';
    const add = mutation && match[1] === 'add';
    let outgoing = options;
    let customAdd = false;
    let tentative = null;
    if (add) {
      let body = options?.body;
      if (body === undefined && input instanceof Request) {
        const copy = input.clone();
        body = (copy.headers.get('content-type') || '').includes('multipart/form-data') ? await copy.formData() : await copy.text();
      }
      const items = requestItems(body);
      const invalid = invalidRequest(items);
      if (invalid) {
        if (formIntent?.origin === 'form' && formIntent.state === 'pending' && items.length === 1 && formIntent.variant === String(invalid.id) && formIntent.quantity === Number(invalid.quantity || 1) && formIntent.otherIdentity === withoutMeasurementIdentity(invalid)) finishFormIntent(formIntent, false);
        notice(text.chooseOnProduct, productURL(invalid.id), text.chooseSize);
        return new Response(JSON.stringify({status: 422, message: text.required, description: text.chooseOnProduct}), {status: 422, headers: {'Content-Type': 'application/json'}});
      }
      customAdd = items.some(item => custom[String(item.id)]);
      if (customAdd) tentative = matchingFormIntent(items);
      outgoing = {...options, body: cleanStandardProperties(body)};
      if (input instanceof Request) {
        const headers = new Headers(options?.headers || input.headers);
        if (outgoing.body instanceof FormData) headers.delete('content-type');
        outgoing.headers = headers;
      }
    }
    if (!mutation) return nativeFetch(input, outgoing);
    if (customAdd) markCustomIntent();
    const scoped = customContext();
    if (scoped && recovery.some(item => !item.acknowledged)) {
      recoveryNotice();
      throw new Error(text.cartUncertain || text.cartError);
    }
    const operation = tentative ? {...tentative, origin: 'fetch', awaitingSubmit: false} : {id: Date.now() + '-' + (++mutationSequence), at: Date.now(), kind: match[1], custom: scoped, state: 'pending'};
    if (tentative) {
      clearTimeout(tentative.timer);
      if (tentative.form) formIntents.delete(tentative.form);
    }
    mutations.set(operation.id, operation);
    cartGeneration++;
    let timer;
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        operation.state = 'uncertain';
        if (operation.custom || customContext()) { rememberUncertain(operation); recoveryNotice(); }
        reject(new Error(text.cartUncertain || text.cartError));
      }, 8000);
    });
    const request = Promise.resolve().then(() => nativeFetch(input, outgoing));
    const observed = request.then(response => {
      clearTimeout(timer);
      mutations.delete(operation.id);
      updateRecovery(items => items.filter(item => item.id !== operation.id));
      cartGeneration++;
      if (operation.custom) customHint = true;
      renderCartState();
      scheduleRefresh();
      return response;
    }, error => {
      clearTimeout(timer);
      operation.state = 'uncertain';
      if (operation.custom || customContext()) { rememberUncertain(operation); recoveryNotice(); }
      cartGeneration++;
      throw error;
    });
    if (scoped) return Promise.race([observed, timeout]);
    timeout.catch(() => {});
    return observed;
  };

  async function checkCheckout(target, form) {
    if (checkoutIntent || Date.now() < replayUntil) return;
    if (checkWriteState()) return;
    const intent = {};
    checkoutIntent = intent;
    try {
      await refreshCart();
      if (checkoutIntent !== intent || verifiedGeneration !== cartGeneration) throw new Error('Cart changed');
      if (checkWriteState()) return;
      if (invalidItems().length) { notice(text.cartMissing, root + 'cart#zen-bracelet-cart', text.fixCart); return; }
      if (!target.isConnected || (form && !form.isConnected) || target.disabled) throw new Error('Checkout control changed');
      let submitted = !form;
      const observeSubmit = () => { submitted = true; };
      if (form) form.addEventListener('submit', observeSubmit, {once: true});
      checkoutPass.add(target);
      try {
        if (form) form.requestSubmit(target.matches?.('button,input') ? target : undefined);
        else target.click();
      } finally {
        checkoutPass.delete(target);
        if (form) form.removeEventListener('submit', observeSubmit);
      }
      if (submitted) replayUntil = Date.now() + 1000;
    } catch { notice(text.cartError, root + 'cart', text.viewCart); }
    finally { if (checkoutIntent === intent) checkoutIntent = null; }
  }

  function stop(event) { event.preventDefault(); event.stopImmediatePropagation(); }

  function onPurchaseClick(event) {
    const target = event.target.closest?.('button,input[type="submit"],a,[role="button"]');
    if (!target) return;
    if (checkoutPass.has(target)) { checkoutPass.delete(target); return; }
    if (target.matches('[name="checkout"],.qsc2-checkout-button,a[href*="/checkout"]')) {
      if (customContext()) { stop(event); checkCheckout(target, target.form); }
      return;
    }
    if (target.matches('.qsc2-product-item__variant-editor')) {
      const row = target.closest('.qsc2-product-item');
      const link = row?.querySelector('a[href*="/products/"]');
      const handle = link?.getAttribute('href')?.split('/products/')[1]?.split('?')[0];
      if (config.handles.includes(handle)) {
        stop(event); notice(text.editOnProduct, root + 'cart#zen-bracelet-cart', text.fixCart); return;
      }
    }
    if (target.matches('.qsc2-stickybar__action-btn')) {
      const forms = [...document.querySelectorAll('form[action*="/cart/add"]')].filter(f => f.querySelector('[data-zen-product-size]') && !f.closest('.modal'));
      if (forms.length === 1 && custom[variantId(forms[0])]) {
        stop(event); syncForm(forms[0]);
        const input = forms[0].querySelector('[data-zen-measurement]');
        input.focus(); input.scrollIntoView?.({block: 'center', behavior: 'auto'}); return;
      }
    }
    const form = target.form || target.closest('form[action*="/cart/add"]');
    if (form && form.action.includes('/cart/add') && target.matches('[name="add"],[data-add-to-cart],.add-to-cart')) {
      if (!validateForm(form)) stop(event);
      else if (custom[variantId(form)]) {
        markCustomIntent();
        if (checkWriteState()) stop(event);
        else if (form.noValidate || form.checkValidity()) beginFormIntent(form, true);
      }
    }
  }

  document.addEventListener('click', onPurchaseClick, true);
  document.addEventListener('keyup', event => {
    if (event.key === 'Enter' && event.target.matches?.('.qsc2-product-item__variant-editor')) onPurchaseClick(event);
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.action?.includes('/cart/add')) {
      if (!validateForm(form)) stop(event);
      else if (custom[variantId(form)]) {
        markCustomIntent();
        const intent = formIntents.get(form);
        if (intent?.awaitingSubmit && intent.state === 'pending' && Date.now() - intent.at < 1000 && intent.variant === variantId(form)) { intent.awaitingSubmit = false; exposeFormIntent(intent); }
        else if (checkWriteState()) stop(event);
        else beginFormIntent(form, false);
      }
    } else if (form.id === 'CartPageForm' || form.id === 'CartDrawerForm') {
      const target = event.submitter || form.querySelector('[name="checkout"]');
      if (target && (event.submitter?.name === 'checkout' || !event.submitter)) {
        if (checkoutPass.has(target)) { checkoutPass.delete(target); return; }
        if (customContext()) { stop(event); checkCheckout(target, form); }
      }
    }
  }, true);
  document.addEventListener('invalid', event => {
    if (event.target.matches?.('[data-zen-measurement]')) {
      event.preventDefault(); fieldError(event.target, measurement(event.target.value).error || text.invalid, true);
    }
  }, true);
  document.addEventListener('input', event => {
    if (event.target.matches?.('[data-zen-measurement]')) fieldError(event.target, '');
  });
  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-variant-input],[name="id"]')) queueMicrotask(initializeForms);
  });
  document.addEventListener('variant:change', () => queueMicrotask(initializeForms));

  function cartLineIdentity(item, properties = item.properties) {
    const sorted = Object.fromEntries(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
    return JSON.stringify([String(item.variant_id || item.id), String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''), sorted]);
  }

  function withoutMeasurementIdentity(item) {
    const properties = {...(item.properties || {})};
    delete properties[property];
    return cartLineIdentity(item, properties);
  }

  function cartQuantities(items) {
    const totals = new Map();
    items.forEach(item => {
      const identity = cartLineIdentity(item);
      totals.set(identity, (totals.get(identity) || 0) + Number(item.quantity));
    });
    return JSON.stringify([...totals].sort(([a], [b]) => a.localeCompare(b)));
  }

  async function saveCartMeasurement(button) {
    const field = button.closest('[data-zen-cart-size]');
    const input = field.querySelector('[data-zen-measurement]');
    const answer = measurement(input.value);
    fieldError(input, answer.ok ? '' : answer.error, !answer.ok);
    if (!answer.ok || checkWriteState()) return;
    button.disabled = true;
    try {
      await refreshCart();
      if (verifiedGeneration !== cartGeneration || checkWriteState()) throw new Error('Cart changed');
      const item = cart.items.find(row => row.key === field.dataset.key);
      if (!item || !custom[String(item.variant_id)]) throw new Error('Cart item changed');
      const properties = {...(item.properties || {}), [property]: answer.value};
      const expected = cartQuantities(cart.items.map(row => row.key === item.key ? {...row, properties} : row));
      const response = await window.fetch(root + 'cart/change.js', {
        method: 'POST', headers: {'Content-Type': 'application/json'}, credentials: 'same-origin',
        body: JSON.stringify({id: item.key, quantity: item.quantity, properties})
      });
      if (!response.ok) throw new Error('Cart save failed');
      const updated = await refreshCart();
      if (expected !== cartQuantities(updated.items)) throw new Error('Readback mismatch');
      location.reload();
    } catch {
      fieldError(input, text.saveError, true);
      if (recovery.some(item => !item.acknowledged)) recoveryNotice();
    } finally { button.disabled = false; }
  }

  function initializeForms() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(syncForm);
    document.querySelectorAll('[data-zen-save-size]').forEach(button => {
      if (button.dataset.zenBound) return;
      button.dataset.zenBound = 'true';
      const field = button.closest('[data-zen-cart-size]');
      const input = field.querySelector('[data-zen-measurement]');
      const unique = 'ZenCartSize-' + (++cartFieldIndex);
      input.id = unique;
      field.querySelector('label').htmlFor = unique;
      const help = field.querySelector('.zen-size-help');
      const error = field.querySelector('[data-zen-size-error]');
      help.id = unique + '-help'; error.id = unique + '-error';
      input.setAttribute('aria-describedby', help.id + ' ' + error.id);
      button.addEventListener('click', () => saveCartMeasurement(button));
    });
  }

  let cartFieldIndex = 0;
  if (window.Qsc2Data?.settings?.data) window.Qsc2Data.settings.data.sc_custom_properties_enable = true;
  initializeForms(); renderCartState();
  if (recovery.some(item => !item.acknowledged)) recoveryNotice();
  if (location.hash === '#zen-bracelet-size') {
    const input = document.querySelector('[data-zen-product-size]:not([hidden]) [data-zen-measurement]');
    input?.focus();
    input?.scrollIntoView?.({block: 'center'});
  }
  const observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('form,[data-zen-save-size]') || node.querySelector?.('form,[data-zen-save-size]'))))) initializeForms();
  });
  observer.observe(document.body, {childList: true, subtree: true});
  ['cart:updated', 'ajaxProduct:added', 'cart:change', 'cart:add', 'cart-drawer:open', 'cart-drawer:loaded', 'pageshow'].forEach(name => {
    document.addEventListener(name, observeCartEvent);
  });
  window.addEventListener('pageshow', () => {
    checkoutIntent = null;
    replayUntil = 0;
    scheduleRefresh();
    if (recovery.some(item => !item.acknowledged)) recoveryNotice();
  });
  window.ZenBraceletSizing = {measurement, validateForm, syncForm, requestItems, invalidRequest, cleanStandardProperties, property, refreshCart};
})();
